/**
 * Number Formatting Utilities for Analytics Hub
 */

/**
 * Format a number in compact notation
 * 1234 → "1.2K", 1234567 → "1.2M", 1234567890 → "1.2B"
 */
export function formatCompactNumber(value: number): string {
  if (value === 0) return '0';

  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  if (absValue >= 1_000_000_000) {
    return `${sign}${(absValue / 1_000_000_000).toFixed(1).replace(/\.0$/, '')}B`;
  }
  if (absValue >= 1_000_000) {
    return `${sign}${(absValue / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  }
  if (absValue >= 1_000) {
    return `${sign}${(absValue / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
  }

  return `${sign}${absValue}`;
}

/**
 * Format a number as percentage
 * 3.14159 → "3.14%"
 */
export function formatPercent(value: number): string {
  return `${value.toFixed(2)}%`;
}

/**
 * Calculate and format a delta between current and previous values
 * Returns formatted string with sign and whether it's positive
 */
export function formatDelta(
  current: number,
  previous: number
): { value: string; isPositive: boolean; isZero: boolean } {
  if (previous === 0 && current === 0) {
    return { value: '0%', isPositive: false, isZero: true };
  }

  if (previous === 0) {
    return { value: '+100%', isPositive: true, isZero: false };
  }

  const change = ((current - previous) / previous) * 100;
  const isPositive = change > 0;
  const isZero = change === 0;
  const sign = isPositive ? '+' : '';

  return {
    value: `${sign}${change.toFixed(1)}%`,
    isPositive,
    isZero,
  };
}
